import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Loader2, Lock, Wrench } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { heroImages } from '../mock/data';

// Wraps the whole app: handles maintenance and private modes.
const SiteGuard = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { settings, loading: settingsLoading } = useSiteSettings();
  const loc = useLocation();

  const isLoading = authLoading || settingsLoading;
  const status = settings?.site_status || 'public';
  const isAdmin = !!user?.is_admin;

  // Admin, login/signup, and account pages always accessible
  const isAuthPath = loc.pathname.startsWith('/login') || loc.pathname.startsWith('/signup') || loc.pathname.startsWith('/admin');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" />
      </div>
    );
  }

  // Maintenance mode: only admins can view (auth pages still work)
  if (status === 'maintenance' && !isAdmin && !loc.pathname.startsWith('/admin') && !loc.pathname.startsWith('/login')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white relative overflow-hidden">
        <img src={heroImages.warehouse} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10 max-w-2xl mx-auto text-center px-6">
          <div className="w-16 h-16 rounded-full bg-[#c8102e] flex items-center justify-center mx-auto mb-6">
            <Wrench className="w-7 h-7 text-white" />
          </div>
          <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Maintenance</span><span className="h-px w-10 bg-[#c8102e]" /></div>
          <h1 className="font-serif-display text-5xl md:text-6xl font-bold mb-4 leading-[1.05]">We'll be back shortly.</h1>
          <p className="text-white/70 text-lg mb-8">Our website is undergoing planned improvements. In the meantime, please contact us for anything urgent.</p>
          <Link to="/login" className="text-sm text-white/60 hover:text-[#c8102e]">Admin sign in →</Link>
        </div>
      </div>
    );
  }

  // Private mode: only logged-in users can view (or admin)
  if (status === 'private' && !user && !isAuthPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden">
        <img src={heroImages.counter} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/90 to-white/80" />
        <div className="relative z-10 max-w-2xl mx-auto text-center px-6">
          <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Private Site</span><span className="h-px w-10 bg-[#c8102e]" /></div>
          <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-black mb-4 leading-[1.05]">Members only access.</h1>
          <p className="text-neutral-600 mb-8">This website is currently private. Please sign in to continue, or create an account if you don't have one yet.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/login" className="bg-[#c8102e] hover:bg-black text-white h-12 px-8 inline-flex items-center text-[13px] tracking-[0.15em] uppercase font-semibold transition-colors">Sign In</Link>
            <Link to="/signup" className="border border-black hover:bg-black hover:text-white h-12 px-8 inline-flex items-center text-[13px] tracking-[0.15em] uppercase font-semibold transition-colors">Sign Up</Link>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default SiteGuard;
